import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { PageLayout } from '@ipfabric/ui-components/PageLayout/PageLayout';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { Policy } from '@ipfabric/utils';
import { Button } from 'components/Button/Button';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { type FC, memo, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocationState } from 'utils/history/useLocationState';
import { useConfirmDialog } from 'utils/hooks/useConfirmDialog';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';
import { AddPolicy } from './AddPolicy';
import { EditPolicy } from './EditPolicy';
import { PoliciesTable } from './PoliciesTable';

const BarePoliciesPage: FC = () => {
  const { administrationPolicies: policiesMsg } = useLocale();
  const [policyToRemove, setPolicyToRemove] = useState<Policy | null>(null);
  const [deleting, setDeleting] = useLoadingState();
  const api = useApi();
  const notifications = useNotifications();
  const [policyToEdit, setPolicyToEdit] = useLocationState<Policy | null>(
    'policyToEdit',
    null,
  );
  const [addPolicy, setAddPolicy] = useLocationState<boolean>(
    'addPolicy',
    false,
  );
  const [policyToCopy, setPolicyToCopy] = useState<Policy | null>(null);

  const deletePolicyConfirmDialog = useConfirmDialog({
    name: 'DELETE_POLICY_ROW_CONFIRM',
  });

  const title = (() => {
    if (addPolicy) {
      return policiesMsg.titleAdd;
    }
    if (policyToEdit !== null) {
      return policyToEdit.isSystem
        ? policiesMsg.titleDetail
        : policiesMsg.titleEdit;
    }
    if (policyToCopy !== null) {
      return policiesMsg.titleCopy;
    }
    return null;
  })();

  const removePolicy = async () => {
    await withLoadingIndicator(setDeleting)(async () => {
      const deleteUrl =
        policyToRemove &&
        {
          apiScopes: '/policies/scopes/api',
          attributeScopes: '/policies/scopes/attributes',
        }[policyToRemove.scopeType];

      await api.request({
        method: 'DELETE',
        url: `${deleteUrl}/${policyToRemove?.id}`,
      });
      notifications.showNotification({
        type: 'success',
        message: policiesMsg.deleteNotification,
      });
      setPolicyToRemove(null);
      deletePolicyConfirmDialog.closeConfirmDialog();
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies(deletePolicyConfirmDialog.toggleConfirmDialog): stable callback
  useEffect(() => {
    if (policyToRemove !== null) {
      deletePolicyConfirmDialog.toggleConfirmDialog();
    }
  }, [policyToRemove]);

  const renderContent = () => {
    if (policyToEdit || policyToCopy) {
      return (
        <PageLayout className='flex flex-col'>
          <h2>{title}</h2>
          <EditPolicy
            policyToCopy={policyToCopy}
            policyToEdit={policyToEdit}
            onCancel={() => {
              setPolicyToEdit(null);
              setPolicyToCopy(null);
            }}
          />
        </PageLayout>
      );
    }
    if (addPolicy) {
      return (
        <PageLayout className='flex flex-col'>
          <h2>{title}</h2>
          <AddPolicy
            onCancel={() => {
              setAddPolicy(false);
            }}
          />
        </PageLayout>
      );
    }
    if (deleting === 'LOADING') {
      return <LoaderAndText />;
    }
    return (
      <div className='flex flex-col flex-1 gap p'>
        <div>
          <Button
            variant='secondary'
            icon='fa-plus'
            onClick={() => {
              setAddPolicy(true);
            }}
            dataTestId='add-policy-button'
            type='button'
          >
            {policiesMsg.addBtn}
          </Button>
        </div>
        <PoliciesTable
          onRemove={setPolicyToRemove}
          onEdit={(policy: Policy) => {
            setPolicyToEdit(policy);
          }}
          onCopy={(policy: Policy) => {
            setPolicyToCopy(policy);
          }}
        />
      </div>
    );
  };

  return (
    <>
      <Helmet title={title ?? 'Policies'} />
      <deletePolicyConfirmDialog.DialogComponent
        msg={policiesMsg.deleteConfirmDialog}
        onClickConfirm={() => {
          if (policyToRemove === null) {
            stumble('no policy is set to be removed');
          } else {
            removePolicy();
          }
        }}
        onCloseClick={() => {
          setPolicyToRemove(null);
          setPolicyToCopy(null);
        }}
        btnConfirmAccent='negative'
        btnConfirmVariant='primary'
        btnCancelVariant='secondary'
      />
      {renderContent()}
    </>
  );
};

export const PoliciesPage = memo(BarePoliciesPage);
